import { useCallback, useEffect, useState, type ReactNode } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { motion } from "motion/react";
import { Page, Screen, ScreenHeader, Section } from "../components/AppShell";
import { Button, IconButton, Skeleton, StateMessage } from "../components/ui";
import { ConfirmDialog } from "../components/ConfirmDialog";
import { Icon } from "../components/Icon";
import { useToast } from "../components/Toast";
import { MediaHero } from "../features/media/MediaHero";
import { CachedMedia } from "../features/media/CachedMedia";
import { AttachmentList } from "../features/media/AttachmentList";
import { useMediaRefresh } from "../features/media/useMediaRefresh";
import { PriceBreakdown } from "../features/prices/PriceBreakdown";
import { quote } from "../features/prices/quote";
import { PublicPageSection } from "../features/share/PublicPageSection";
import { useDeletePiece, usePiece, usePrices, useRestorePiece } from "../api/queries";
import { haptic } from "../telegram/haptics";
import { fadeUp, listItem, sectionStagger } from "../motion";
import { fieldLabels, type FieldLabel } from "../i18n/fieldLabels";
import {
  clarityLabels,
  conditionLabels,
  currencyLabels,
  cutLabels,
  kindLabels,
  labelOf,
  metalColorLabels,
  metalLabels,
  ownershipLabels,
  purityLabel,
  stoneShapeLabels,
  stoneTypeLabels,
} from "../i18n/choices";
import { useT } from "../i18n/strings";
import { pick } from "../i18n/labels";
import { formatDate, formatGrams, formatMoney, formatNumber, isLatinScript } from "../i18n/format";
import { messagesOf } from "../api/errors";
import type { Stone } from "../api/types";
import s from "../features/pieces/detail.module.css";

export default function Detail() {
  const t = useT();
  const { id = "" } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const [confirming, setConfirming] = useState(false);
  /** Which media file the hero is showing; null means the thumbnail, as loaded. */
  const [viewing, setViewing] = useState<string | null>(null);

  const query = usePiece(id);
  const prices = usePrices();
  const remove = useDeletePiece();
  const restore = useRestorePiece();

  const refresh = useCallback(() => void query.refetch(), [query]);
  const refreshMedia = useMediaRefresh(refresh);

  const primaryId = query.data?.attachments.find((file) => file.kind === "primary")?.id ?? null;
  useEffect(() => {
    setViewing(null);
  }, [id, primaryId]);

  const onDelete = () => {
    setConfirming(false);
    remove.mutate(id, {
      onSuccess: () => {
        haptic.success();
        navigate("/", { replace: true });
        toast.show(t.detail.deleted, {
          action: {
            label: t.actions.undo,
            run: () =>
              restore.mutate(id, {
                onSuccess: () => toast.show(t.detail.restored),
              }),
          },
        });
      },
      onError: (error) => toast.show(messagesOf(error)[0] ?? t.errors.title, { tone: "error" }),
    });
  };

  if (query.isPending) return <DetailSkeleton />;

  if (query.error || !query.data) {
    return (
      <Screen withTabs={false}>
        <ScreenHeader title="" />
        <StateMessage
          icon="alert"
          title={t.errors.title}
          body={query.error ? messagesOf(query.error)[0] : t.errors.notFound}
          action={
            <Button variant="secondary" onClick={() => navigate("/")}>
              {t.tabs.pieces}
            </Button>
          }
        />
      </Screen>
    );
  }

  const piece = query.data;
  const primary = piece.attachments.find((file) => file.kind === "primary") ?? null;
  const media = primary
    ? [primary, ...piece.attachments.filter((file) => file.kind === "gallery")]
    : piece.attachments.filter((file) => file.kind === "gallery");
  const shown = media.find((file) => file.id === viewing) ?? primary;
  const conditionFiles = piece.attachments.filter((file) => file.kind === "condition_report");
  const evidenceFiles = piece.attachments.filter((file) => file.kind === "authenticity_evidence");
  const latin = isLatinScript(piece.title);
  const grams = (value: number | null) =>
    value === null ? null : <Measure value={formatGrams(value)} unit={t.form.gram} />;

  const showMake = Boolean(piece.maker || piece.size_label || piece.year_display);
  const showCondition =
    Boolean(piece.condition) || Boolean(piece.condition_notes) || conditionFiles.length > 0;
  const showAuthenticity = Boolean(piece.authenticity_notes) || evidenceFiles.length > 0;
  const subtitle = [
    labelOf(kindLabels, piece.kind),
    purityLabel(piece.purity_permille, piece.metal),
    piece.year_display,
  ].filter(Boolean);

  return (
    <Screen withTabs={false}>
      <ScreenHeader
        overlay
        actions={
          <IconButton
            name="edit"
            label={t.actions.edit}
            className="chrome-media"
            onClick={() => navigate(`/piece/${piece.id}/edit`)}
          />
        }
      />

      <MediaHero
        media={shown}
        layoutId={shown?.id === primary?.id && primary ? `hero-${primary.id}` : undefined}
        onExpired={refreshMedia}
      />

      {media.length > 1 && (
        <div className={s.strip}>
          {media.map((file) => (
            <button
              key={file.id}
              type="button"
              className={[s.stripTile, file.id === shown?.id && s.stripTileOn]
                .filter(Boolean)
                .join(" ")}
              aria-pressed={file.id === shown?.id}
              aria-label={file.original_filename || file.mime_type}
              onClick={() => {
                haptic.tap();
                setViewing(file.id);
              }}
            >
              <CachedMedia
                media={file}
                prefer="thumbnail"
                className={s.stripPreview}
                fallback={<Icon name="image" size={20} />}
              />
            </button>
          ))}
        </div>
      )}

      <motion.div variants={sectionStagger} initial="hidden" animate="show">
        <motion.div className={s.titleBlock} variants={listItem}>
          <h1 className={[s.title, latin && s.titleLatin, "bidi"].filter(Boolean).join(" ")}>
            {piece.title || t.detail.untitled}
          </h1>
          {subtitle.length > 0 && (
            <p className={s.subtitle}>
              {subtitle.map((part, index) => (
                <span key={part} style={{ display: "contents" }}>
                  {index > 0 && <span className={s.dot} />}
                  <span className={index === 0 ? "bidi" : "mono"}>{part}</span>
                </span>
              ))}
            </p>
          )}
        </motion.div>

        <Page>
          <motion.div variants={listItem}>
            <Section title={t.form.sections.metal} icon="metal">
              <div className={s.rows}>
                <Row label={fieldLabels.metal}>{labelOf(metalLabels, piece.metal)}</Row>
                <Row label={fieldLabels.metal_color}>
                  {labelOf(metalColorLabels, piece.metal_color)}
                </Row>
                <Row label={fieldLabels.purity_permille}>
                  {piece.purity_permille !== null && (
                    <span className="mono">{purityLabel(piece.purity_permille, piece.metal)}</span>
                  )}
                </Row>
                <Row label={fieldLabels.gross_weight_g}>{grams(piece.gross_weight_g)}</Row>
                <Row label={fieldLabels.net_weight_g}>{grams(piece.net_weight_g)}</Row>
                <Row label={fieldLabels.hallmarks}>
                  {piece.hallmarks && <span className="bidi">{piece.hallmarks}</span>}
                </Row>
              </div>
            </Section>
          </motion.div>

          {piece.stones.length > 0 && (
            <motion.div variants={listItem}>
              <Section title={t.form.sections.stones} icon="stone">
                <div className={s.rows}>
                  {piece.stones.map((stone, index) => (
                    <StoneRow key={stone.id ?? index} stone={stone} />
                  ))}
                </div>
              </Section>
            </motion.div>
          )}

          {showMake && (
            <motion.div variants={listItem}>
              <Section title={t.form.sections.make} icon="maker">
                <div className={s.rows}>
                  <Row label={fieldLabels.maker}>
                    {piece.maker && <span className="bidi">{piece.maker}</span>}
                  </Row>
                  <Row label={fieldLabels.size_label}>
                    {piece.size_label && <span className="bidi">{piece.size_label}</span>}
                  </Row>
                  <Row label={fieldLabels.year}>
                    {piece.year_display && <span className="mono">{piece.year_display}</span>}
                  </Row>
                </div>
              </Section>
            </motion.div>
          )}

          <motion.div variants={listItem}>
            <Section title={t.form.sections.ownership} icon="ownership">
              <div className={s.rows}>
                <Row label={fieldLabels.ownership_status}>
                  {piece.ownership_status && (
                    <StatusChip text={labelOf(ownershipLabels, piece.ownership_status)} />
                  )}
                </Row>
                <Row label={fieldLabels.making_charge_percent}>
                  {piece.making_charge_percent !== null && (
                    <Measure value={formatNumber(piece.making_charge_percent)} unit="%" />
                  )}
                </Row>
              </div>
              <PriceBreakdown
                quote={quote(piece, prices.data)}
                makingPercent={piece.making_charge_percent}
                board={prices.data}
                loading={prices.isPending}
                frozen={
                  piece.sold_price_rial !== null
                    ? { rial: piece.sold_price_rial, at: piece.sold_priced_at }
                    : null
                }
              />
            </Section>
          </motion.div>

          {showCondition && (
            <motion.div variants={listItem}>
              <Section title={t.form.sections.condition} icon="condition">
                {piece.condition && <StatusChip text={labelOf(conditionLabels, piece.condition)} />}
                {piece.condition_notes && <p className={s.notes}>{piece.condition_notes}</p>}
                {conditionFiles.length > 0 && <AttachmentList files={conditionFiles} />}
              </Section>
            </motion.div>
          )}

          {piece.provenance_text && (
            <motion.div variants={listItem}>
              <Section title={t.form.sections.provenance} icon="provenance">
                <p className={s.notes}>{piece.provenance_text}</p>
              </Section>
            </motion.div>
          )}

          {showAuthenticity && (
            <motion.div variants={listItem}>
              <Section title={t.form.sections.authenticity} icon="authenticity">
                {piece.authenticity_notes && <p className={s.notes}>{piece.authenticity_notes}</p>}
                {evidenceFiles.length > 0 && <AttachmentList files={evidenceFiles} />}
                {/* A certificate on file is evidence. It is not a verdict, and no
                    copy in this app is allowed to imply that it is. */}
                <p className={s.evidenceNote}>
                  <Icon name="info" size={16} />
                  <span>{t.detail.authenticityNote}</span>
                </p>
              </Section>
            </motion.div>
          )}

          {piece.notes && (
            <motion.div variants={listItem}>
              <Section title={t.form.sections.notes} icon="condition">
                <p className={s.notes}>{piece.notes}</p>
              </Section>
            </motion.div>
          )}

          <motion.div variants={listItem}>
            <PublicPageSection piece={piece} />
          </motion.div>

          <div className={s.footMeta}>
            {piece.inventory_code && (
              <span>
                {pick(fieldLabels.inventory_code)}:{" "}
                <span className={s.idLine}>{piece.inventory_code}</span>
              </span>
            )}
            <span>
              ID <span className={s.idLine}>{piece.id}</span>
            </span>
            <span>
              {pick(fieldLabels.created_at)}: {formatDate(piece.created_at)}
            </span>
            <span>
              {pick(fieldLabels.updated_at)}: {formatDate(piece.updated_at)}
            </span>
          </div>

          <div className={s.deleteRow}>
            <Button
              variant="destructive"
              icon="delete"
              onClick={() => setConfirming(true)}
              style={{ width: "100%" }}
            >
              {t.actions.delete}
            </Button>
          </div>
        </Page>
      </motion.div>

      <ConfirmDialog
        open={confirming}
        title={t.actions.confirmDelete}
        body={t.detail.deletePrompt}
        onConfirm={onDelete}
        onCancel={() => setConfirming(false)}
      />
    </Screen>
  );
}

/* ------------------------------------------------------------------ bits -- */

function StoneRow({ stone }: { stone: Stone }) {
  const t = useT();
  const head = [labelOf(stoneTypeLabels, stone.stone_type), labelOf(stoneShapeLabels, stone.shape)]
    .filter(Boolean)
    .join(" · ");
  const grades = [
    stone.carat !== null ? t.detail.carat(formatNumber(stone.carat)) : "",
    stone.color,
    labelOf(clarityLabels, stone.clarity),
    labelOf(cutLabels, stone.cut),
  ].filter(Boolean);
  return (
    <div className={s.row}>
      <span className={s.rowLabel}>
        {head} <span className="mono">{t.detail.stoneCount(stone.count)}</span>
      </span>
      <span className={s.rowValue}>
        {grades.length > 0 && <span className="bidi">{grades.join(" · ")}</span>}
        {stone.price !== null && (
          <>
            {grades.length > 0 && <br />}
            <span className={s.rowValueMuted}>
              {t.form.stonePrice}: <span className="mono">{formatMoney(stone.price)}</span>{" "}
              {labelOf(currencyLabels, stone.currency)}
            </span>
          </>
        )}
        {stone.certificate_ref && (
          <>
            <br />
            <span className={s.rowValueMuted}>
              {pick(fieldLabels.certificate_ref)}:{" "}
              <span className={s.idLine}>{stone.certificate_ref}</span>
            </span>
          </>
        )}
        {stone.notes && (
          <>
            <br />
            <span className={s.rowValueMuted}>{stone.notes}</span>
          </>
        )}
      </span>
    </div>
  );
}

/** Renders nothing when the field is empty; a catalog of «—» reads as broken. */
function Row({ label: fieldLabel, children }: { label: FieldLabel; children: ReactNode }) {
  const empty =
    children === null || children === undefined || children === false || children === "";
  if (empty) return null;
  return (
    <div className={s.row}>
      <span className={s.rowLabel}>{pick(fieldLabel)}</span>
      <span className={s.rowValue}>{children}</span>
    </div>
  );
}

function Measure({ value, unit }: { value: string; unit: string }) {
  return (
    <span>
      <span className="mono">{value}</span> <span className={s.rowValueMuted}>{unit}</span>
    </span>
  );
}

function StatusChip({ text }: { text: string }) {
  return (
    <span className={`${s.statusChip} paper`}>
      <span className={s.statusDot} />
      {text}
    </span>
  );
}

function DetailSkeleton() {
  return (
    <Screen withTabs={false}>
      <motion.div variants={fadeUp} initial="hidden" animate="show">
        <Skeleton height={280} radius="0" />
        <Page>
          <div style={{ display: "grid", gap: "var(--space-3)", paddingBlock: "var(--space-5)" }}>
            <Skeleton height={26} width="70%" radius="4px" />
            <Skeleton height={16} width="45%" radius="4px" />
            <Skeleton height={120} radius="var(--radius-card)" />
            <Skeleton height={120} radius="var(--radius-card)" />
          </div>
        </Page>
      </motion.div>
    </Screen>
  );
}
